import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Save, Trash2 } from "lucide-react";
import { api } from "../lib/api";
import { ImageField } from "./ImageField";

const DEFAULTS = {
  hero_tile: {
    enabled: true,
    eyebrow: "This week",
    title: "Overton Park & Crosstown",
    subtitle: "Sat – Sun · Served cold",
  },
  about: {
    eyebrow: "Our Story",
    heading: "Made with a love for the city we call home.",
    body: "Memphis Coffee Co. started with a cooler, a few bottles, and a simple idea: serve delicious coffee to everyday people around the city. Parks, pop-ups, neighborhoods, and wherever Memphis gathers.",
    image_url: "",
    stats: [
      { id: "s1", label: "Bottled in", value: "Memphis, TN" },
      { id: "s2", label: "Pop-ups", value: "Every weekend" },
      { id: "s3", label: "Ingredients", value: "Kept simple" },
    ],
  },
  popups_section: {
    eyebrow: "Where to Find Us",
    heading: "Upcoming pop-ups around Memphis.",
    subheading: "Follow along for this week's locations.",
    instagram_label: "Follow on Instagram",
    instagram_url: "#",
    bento_image_url: "",
    bento_eyebrow: "Out & About",
    bento_title: "Bottled here. Shared with the city.",
    empty: {
      enabled: true,
      title: "We're between pop-ups at the moment.",
      body: "Check back soon to see where Memphis Coffee Co is headed next!",
    },
  },
};

const SECTIONS = [
  { id: "hero", label: "Hero tile" },
  { id: "about", label: "Our Story" },
  { id: "popups", label: "Where to Find Us" },
];

const tmpId = () => `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

export const SiteSettingsManager = () => {
  const [settings, setSettings] = useState(DEFAULTS);
  const [section, setSection] = useState("hero");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/settings");
        if (!cancelled && r.data) {
          setSettings({
            hero_tile: { ...DEFAULTS.hero_tile, ...(r.data.hero_tile || {}) },
            about: { ...DEFAULTS.about, ...(r.data.about || {}) },
            popups_section: {
              ...DEFAULTS.popups_section,
              ...(r.data.popups_section || {}),
              empty: { ...DEFAULTS.popups_section.empty, ...((r.data.popups_section || {}).empty || {}) },
            },
          });
        }
      } catch {
        /* keep defaults */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const update = (key, patch) => setSettings((s) => ({ ...s, [key]: { ...s[key], ...patch } }));
  const updateNested = (key, sub, patch) =>
    setSettings((s) => ({ ...s, [key]: { ...s[key], [sub]: { ...(s[key]?.[sub] || {}), ...patch } } }));

  const save = async (ev) => {
    ev.preventDefault();
    setSaving(true);
    try {
      const payload = {
        hero_tile: settings.hero_tile,
        about: {
          ...settings.about,
          stats: (settings.about.stats || []).map((st) => ({
            id: st.id && !st.id.startsWith("tmp-") ? st.id : undefined,
            label: (st.label || "").trim(),
            value: (st.value || "").trim(),
          })).filter((st) => st.label && st.value),
        },
        popups_section: settings.popups_section,
      };
      const r = await api.put("/settings", payload);
      // re-hydrate from server (gives us ids for new stats)
      setSettings({
        hero_tile: r.data.hero_tile,
        about: r.data.about,
        popups_section: {
          ...r.data.popups_section,
          empty: r.data.popups_section.empty || DEFAULTS.popups_section.empty,
        },
      });
      toast.success("Site settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-[#8D99AE] text-sm">Loading settings…</div>;

  return (
    <form data-testid="site-settings-form" onSubmit={save}>
      <div className="inline-flex bg-white border border-[#E8E3DD] rounded-full p-1 mb-6 flex-wrap" role="tablist">
        {SECTIONS.map((s) => (
          <button
            key={s.id}
            type="button"
            data-testid={`settings-section-${s.id}`}
            onClick={() => setSection(s.id)}
            className={`text-sm px-4 sm:px-5 py-2 rounded-full transition-colors ${
              section === s.id ? "bg-[#2C1E16] text-[#FAF9F6]" : "text-[#2C1E16] hover:bg-[#F3EDE4]"
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {section === "hero" && (
        <HeroPanel data={settings.hero_tile} update={(p) => update("hero_tile", p)} />
      )}
      {section === "about" && (
        <AboutPanel
          data={settings.about}
          update={(p) => update("about", p)}
        />
      )}
      {section === "popups" && (
        <PopupsPanel
          data={settings.popups_section}
          update={(p) => update("popups_section", p)}
          updateEmpty={(p) => updateNested("popups_section", "empty", p)}
        />
      )}

      <div className="mt-8 flex justify-end">
        <button
          type="submit"
          data-testid="settings-save"
          disabled={saving}
          className="inline-flex items-center justify-center gap-2 bg-[#2C1E16] text-[#FAF9F6] hover:bg-[#3E2D24] disabled:opacity-60 transition-colors rounded-full px-6 py-3 text-sm font-medium"
        >
          {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} strokeWidth={1.6} />}
          Save changes
        </button>
      </div>
    </form>
  );
};

const HeroPanel = ({ data, update }) => (
  <div className="rounded-3xl border border-[#E8E3DD] bg-white p-6 sm:p-8">
    <h3 className="font-display text-xl text-[#2C1E16]">Hero floating tile</h3>
    <p className="text-sm text-[#5C4E46] mt-1">
      The small white card on the hero image. Use it to highlight where you&rsquo;ll be this week.
    </p>

    <label className="mt-6 inline-flex items-center gap-3 cursor-pointer select-none">
      <input
        data-testid="settings-tile-enabled"
        type="checkbox"
        checked={!!data.enabled}
        onChange={(e) => update({ enabled: e.target.checked })}
        className="h-4 w-4 accent-[#2C1E16]"
      />
      <span className="text-sm text-[#2C1E16]">Show tile on the homepage</span>
    </label>

    <div className="mt-6 grid sm:grid-cols-2 gap-x-6 gap-y-5 max-w-3xl">
      <Field label="Eyebrow">
        <input data-testid="settings-tile-eyebrow" className="admin-input"
          value={data.eyebrow || ""} onChange={(e) => update({ eyebrow: e.target.value })} />
      </Field>
      <Field label="Title">
        <input data-testid="settings-tile-title" className="admin-input"
          value={data.title || ""} onChange={(e) => update({ title: e.target.value })} />
      </Field>
      <Field label="Subtitle" className="sm:col-span-2">
        <input data-testid="settings-tile-subtitle" className="admin-input"
          value={data.subtitle || ""} onChange={(e) => update({ subtitle: e.target.value })} />
      </Field>
    </div>

    {data.enabled && (
      <div className="mt-8">
        <div className="text-[11px] uppercase tracking-[0.18em] text-[#8D99AE] mb-3">Preview</div>
        <div className="rounded-2xl bg-[#F3EDE4] p-8 flex justify-center">
          <div data-testid="settings-tile-preview" className="bg-white rounded-2xl border border-[#E8E3DD] shadow-[0_12px_40px_rgb(0,0,0,0.05)] px-5 py-4 max-w-[15rem]">
            {data.eyebrow && <div className="text-[10px] uppercase tracking-[0.18em] text-[#8D99AE]">{data.eyebrow}</div>}
            {data.title && <div className="font-display text-lg text-[#2C1E16] mt-1">{data.title}</div>}
            {data.subtitle && <div className="text-xs text-[#5C4E46] mt-1">{data.subtitle}</div>}
          </div>
        </div>
      </div>
    )}
  </div>
);

const AboutPanel = ({ data, update }) => {
  const setStat = (id, patch) =>
    update({ stats: (data.stats || []).map((s) => (s.id === id ? { ...s, ...patch } : s)) });
  const addStat = () =>
    update({ stats: [...(data.stats || []), { id: tmpId(), label: "", value: "" }] });
  const removeStat = (id) =>
    update({ stats: (data.stats || []).filter((s) => s.id !== id) });

  return (
    <div className="rounded-3xl border border-[#E8E3DD] bg-white p-6 sm:p-8">
      <h3 className="font-display text-xl text-[#2C1E16]">Our Story section</h3>
      <p className="text-sm text-[#5C4E46] mt-1">
        The "About" block on the homepage. Edit the photo, copy, and the three highlights underneath.
      </p>

      <div className="mt-6 grid md:grid-cols-12 gap-x-8 gap-y-6">
        <div className="md:col-span-5">
          <ImageField
            label="Photo"
            value={data.image_url || ""}
            onChange={(v) => update({ image_url: v })}
            testIdPrefix="settings-about-image"
          />
        </div>
        <div className="md:col-span-7 grid sm:grid-cols-2 gap-x-6 gap-y-5 content-start">
          <Field label="Eyebrow">
            <input data-testid="settings-about-eyebrow" className="admin-input"
              value={data.eyebrow || ""} onChange={(e) => update({ eyebrow: e.target.value })} />
          </Field>
          <div />
          <Field label="Heading" className="sm:col-span-2">
            <input data-testid="settings-about-heading" className="admin-input"
              value={data.heading || ""} onChange={(e) => update({ heading: e.target.value })} />
          </Field>
          <Field label="Body" className="sm:col-span-2">
            <textarea data-testid="settings-about-body" rows={5} className="admin-input resize-none pt-2"
              value={data.body || ""} onChange={(e) => update({ body: e.target.value })} />
          </Field>
        </div>
      </div>

      <div className="mt-10 pt-8 border-t border-[#E8E3DD]">
        <div className="flex items-end justify-between mb-2">
          <div>
            <h4 className="font-display text-lg text-[#2C1E16]">Highlights</h4>
            <p className="text-sm text-[#5C4E46] mt-1">Short label + value pairs (e.g., "Bottled in" → "Memphis, TN"). Add as many as you like.</p>
          </div>
          <button
            type="button"
            onClick={addStat}
            data-testid="settings-about-stat-add"
            className="inline-flex items-center gap-2 bg-[#2C1E16] text-[#FAF9F6] rounded-full px-4 py-2 text-sm hover:bg-[#3E2D24]"
          >
            <Plus size={13} strokeWidth={1.7} />
            Add highlight
          </button>
        </div>

        <div className="mt-4 grid gap-3" data-testid="settings-about-stats-list">
          {(data.stats || []).map((s) => (
            <div key={s.id} data-testid={`settings-about-stat-${s.id}`} className="grid sm:grid-cols-12 gap-3 items-end">
              <div className="sm:col-span-5">
                <Field label="Label">
                  <input data-testid={`settings-about-stat-label-${s.id}`} className="admin-input"
                    value={s.label} placeholder="Bottled in"
                    onChange={(e) => setStat(s.id, { label: e.target.value })} />
                </Field>
              </div>
              <div className="sm:col-span-6">
                <Field label="Value">
                  <input data-testid={`settings-about-stat-value-${s.id}`} className="admin-input"
                    value={s.value} placeholder="Memphis, TN"
                    onChange={(e) => setStat(s.id, { value: e.target.value })} />
                </Field>
              </div>
              <div className="sm:col-span-1 flex justify-end">
                <button
                  type="button"
                  onClick={() => removeStat(s.id)}
                  data-testid={`settings-about-stat-remove-${s.id}`}
                  className="p-2 rounded-full text-[#B33A3A] hover:bg-[#FAF9F6]"
                  title="Remove"
                >
                  <Trash2 size={14} strokeWidth={1.6} />
                </button>
              </div>
            </div>
          ))}
          {(data.stats || []).length === 0 && (
            <p className="text-sm text-[#8D99AE]">No highlights yet. Add one above.</p>
          )}
        </div>
      </div>
    </div>
  );
};

const PopupsPanel = ({ data, update, updateEmpty }) => (
  <div className="space-y-6">
    {/* Section heading */}
    <div className="rounded-3xl border border-[#E8E3DD] bg-white p-6 sm:p-8">
      <h3 className="font-display text-xl text-[#2C1E16]">Section header</h3>
      <p className="text-sm text-[#5C4E46] mt-1">The text above the pop-up cards.</p>
      <div className="mt-6 grid sm:grid-cols-2 gap-x-6 gap-y-5 max-w-3xl">
        <Field label="Eyebrow">
          <input data-testid="settings-popups-eyebrow" className="admin-input"
            value={data.eyebrow || ""} onChange={(e) => update({ eyebrow: e.target.value })} />
        </Field>
        <div />
        <Field label="Heading" className="sm:col-span-2">
          <input data-testid="settings-popups-heading" className="admin-input"
            value={data.heading || ""} onChange={(e) => update({ heading: e.target.value })} />
        </Field>
        <Field label="Subheading" className="sm:col-span-2">
          <input data-testid="settings-popups-subheading" className="admin-input"
            value={data.subheading || ""} onChange={(e) => update({ subheading: e.target.value })} />
        </Field>
      </div>
    </div>

    {/* Instagram link */}
    <div className="rounded-3xl border border-[#E8E3DD] bg-white p-6 sm:p-8">
      <h3 className="font-display text-xl text-[#2C1E16]">Instagram link</h3>
      <p className="text-sm text-[#5C4E46] mt-1">The "Follow on Instagram" link in the corner of the section.</p>
      <div className="mt-6 grid sm:grid-cols-2 gap-x-6 gap-y-5 max-w-3xl">
        <Field label="Link label">
          <input data-testid="settings-popups-ig-label" className="admin-input"
            value={data.instagram_label || ""}
            onChange={(e) => update({ instagram_label: e.target.value })}
            placeholder="Follow on Instagram" />
        </Field>
        <Field label="Instagram URL">
          <input data-testid="settings-popups-ig-url" className="admin-input"
            value={data.instagram_url || ""}
            onChange={(e) => update({ instagram_url: e.target.value })}
            placeholder="https://instagram.com/memphiscoffeeco" />
          <span className="block text-[10px] text-[#8D99AE] mt-1">Leave blank to hide the link entirely.</span>
        </Field>
      </div>
    </div>

    {/* Bento card */}
    <div className="rounded-3xl border border-[#E8E3DD] bg-white p-6 sm:p-8">
      <h3 className="font-display text-xl text-[#2C1E16]">Highlight card (large photo)</h3>
      <p className="text-sm text-[#5C4E46] mt-1">The large photo tile next to your pop-up list (with the "Out & About" caption).</p>
      <div className="mt-6 grid md:grid-cols-12 gap-x-8 gap-y-6">
        <div className="md:col-span-5">
          <ImageField
            label="Photo"
            value={data.bento_image_url || ""}
            onChange={(v) => update({ bento_image_url: v })}
            testIdPrefix="settings-popups-bento-image"
            helpText="Leave blank to hide the highlight card. Max 1.5 MB."
          />
        </div>
        <div className="md:col-span-7 grid gap-y-5 content-start">
          <Field label="Eyebrow (small caption on the photo)">
            <input data-testid="settings-popups-bento-eyebrow" className="admin-input"
              value={data.bento_eyebrow || ""}
              onChange={(e) => update({ bento_eyebrow: e.target.value })}
              placeholder="Out & About" />
          </Field>
          <Field label="Title (large white text on the photo)">
            <input data-testid="settings-popups-bento-title" className="admin-input"
              value={data.bento_title || ""}
              onChange={(e) => update({ bento_title: e.target.value })}
              placeholder="Bottled here. Shared with the city." />
          </Field>
        </div>
      </div>
    </div>

    {/* Empty state */}
    <div className="rounded-3xl border border-[#E8E3DD] bg-white p-6 sm:p-8">
      <h3 className="font-display text-xl text-[#2C1E16]">"Between pop-ups" message</h3>
      <p className="text-sm text-[#5C4E46] mt-1">Shown when you have no upcoming pop-ups scheduled, so the section never looks empty.</p>

      <label className="mt-6 inline-flex items-center gap-3 cursor-pointer select-none">
        <input
          data-testid="settings-popups-empty-enabled"
          type="checkbox"
          checked={!!data.empty?.enabled}
          onChange={(e) => updateEmpty({ enabled: e.target.checked })}
          className="h-4 w-4 accent-[#2C1E16]"
        />
        <span className="text-sm text-[#2C1E16]">Show the message when there are zero pop-ups</span>
      </label>

      <div className="mt-6 grid gap-y-5 max-w-3xl">
        <Field label="Title">
          <input data-testid="settings-popups-empty-title" className="admin-input"
            value={data.empty?.title || ""}
            onChange={(e) => updateEmpty({ title: e.target.value })}
            placeholder="We're between pop-ups at the moment." />
        </Field>
        <Field label="Body">
          <textarea data-testid="settings-popups-empty-body" rows={3} className="admin-input resize-none pt-2"
            value={data.empty?.body || ""}
            onChange={(e) => updateEmpty({ body: e.target.value })}
            placeholder="Check back soon to see where Memphis Coffee Co is headed next!" />
        </Field>
      </div>
    </div>
  </div>
);

const Field = ({ label, children, className = "" }) => (
  <label className={`block ${className}`}>
    <span className="block text-[11px] uppercase tracking-[0.18em] text-[#8D99AE] mb-1">{label}</span>
    {children}
  </label>
);
